import { MenuSection } from "@/components/business/MenuSection";
import { AttendanceSection } from "@/components/business/AttendanceSection";
import { DoctorSection } from "@/components/business/DoctorSection";
import { BookingSection } from "@/components/business/BookingSection";
import { TableSection } from "@/components/business/TableSection";
import { EnquirySection } from "@/components/business/EnquirySection";
import { ServiceVisitSection } from "@/components/business/ServiceVisitSection";
import { ComplaintSection } from "@/components/business/ComplaintSection";
import { PlansBlock } from "@/components/storefront/PlansBlock";
import { HowItWorksBlock } from "@/components/storefront/HowItWorksBlock";
import { GalleryBlock } from "@/components/storefront/GalleryBlock";
import { CoverageCheckSection } from "@/components/business/CoverageCheckSection";
import { localized } from "@/theme/tenantTheme";
import { scrollToSection, sectionElementId } from "@/storefront/scroll";

export { scrollToSection, sectionElementId };

/** Capability -> the existing section component that renders it. `key` is shared by two
 * capabilities (`stay_booking`/`venue_booking` both render as "booking") on purpose — a business
 * cannot need both a stay and a venue booker on one page, and `sectionsFor` dedupes on `key` so a
 * business that somehow carries both still gets exactly one `BookingSection`. */
export const SECTION_REGISTRY = {
  ordering: { key: "menu", cap: "ordering", Component: MenuSection },
  // C5 (storefront S3): "Class batches" + "Book a free trial class", wiring the existing
  // `attendance` module's batch schedule into a public block for the first time (the capability
  // had no storefront section at all before this — `07_storefront_design_gap.md` §5 C5).
  attendance: { key: "attendance", cap: "attendance", Component: AttendanceSection },
  appointments: { key: "doctors", cap: "appointments", Component: DoctorSection },
  stay_booking: { key: "booking", cap: "stay_booking", Component: BookingSection },
  venue_booking: { key: "booking", cap: "venue_booking", Component: BookingSection },
  table_booking: { key: "table", cap: "table_booking", Component: TableSection },
  enquiry: { key: "enquiry", cap: "enquiry", Component: EnquirySection },
  service_visit: { key: "service_visit", cap: "service_visit", Component: ServiceVisitSection },
  complaints: { key: "complaints", cap: "complaints", Component: ComplaintSection },
};

/** Capability-agnostic blocks — no `cap`, so `sectionsFor` cannot gate them on
 * `business.capabilities`. `plans` (PR4) is visible whenever `business.plans` is non-empty,
 * regardless of what the business can otherwise do (ARCHITECTURE.md §5's "PSEUDO: plans,
 * gallery, about"). */
const PSEUDO_REGISTRY = {
  plans: { key: "plans", Component: PlansBlock },
  // F4/L2-004: the ISP coverage check. No `cap` (like `plans`) — it is gated on DATA
  // (`storefront_config.vertical.coverage` non-empty) AND the `enquiry` capability, since it
  // feeds the enquiry-capture form (prefilled) for both a "yes" and a "not yet" result.
  coverage: { key: "coverage", Component: CoverageCheckSection },
  // C4: the numbered "how it works" strip. Gated on DATA (`storefront_config.profile.how_it_works`
  // non-empty), like `plans` — never fabricated, never shown empty.
  how_it_works: { key: "how_it_works", Component: HowItWorksBlock },
  // UAT P2x: `business.gallery` (image URLs) was served but never rendered. Gated on DATA.
  gallery: { key: "gallery", Component: GalleryBlock },
};

/** The order `BusinessMiniSite`'s tabs rendered in before PR3 (menu, doctors, booking, table,
 * enquiry, service-visit, complaint) — NOT the order the PR3 brief sketched (which put
 * `service_visit` ahead of `enquiry`). Kept as the actual current order so parity holds when a
 * business has no `storefront_config.order` override. `plans` leads by default (PR4): the design
 * floor's own registry example orders a vertical's plans first, ahead of the operational
 * sections. */
const DEFAULT_ORDER = [
  "plans",
  "how_it_works",
  "coverage",
  "attendance",
  "ordering",
  "appointments",
  "stay_booking",
  "venue_booking",
  "table_booking",
  "enquiry",
  "service_visit",
  "complaints",
  "gallery",
];

/** The five capabilities `QA/billing-2026-09/BILLING_SPEC.md` §2 calls "online order/booking
 * acceptance" — the single "ordering" `FEATURE_MIN_PLAN` gate (`directory/enums.py`) covers all
 * five, matching `console_views.py`'s own `catalog` view, which already treats them as one
 * editing surface. On Free, these sections are hidden entirely (never shown locked/upsold) so the
 * page falls back to its own Call/WhatsApp contact rail — there is no in-between "you could, but
 * upgrade first" state for a citizen-facing action. */
export const ONLINE_BOOKING_CAPS = new Set([
  "ordering",
  "appointments",
  "stay_booking",
  "venue_booking",
  "table_booking",
]);

/** Whether a capability the business has toggled on is actually LIVE on its public page —
 * exported so anything that lists/badges capabilities (e.g. `AboutSection`'s capability pills,
 * W-02) agrees with `sectionsFor`'s own gate below and the two cannot drift apart again: a
 * Free-plan business used to show an "Online ordering" badge while the matching section was
 * correctly hidden a few lines later on the same page. */
export function isCapabilityLive(business, cap) {
  if (!ONLINE_BOOKING_CAPS.has(cap)) return true;
  if (business.plan === "free") return false;
  // B1: a bookable capability with nothing to book behind it is a dead CTA. The storefront
  // payload carries the real inventory flags; only an explicit `false` hides (a payload that
  // predates the flags, or a list row that never carries them, stays as it was).
  const flag = INVENTORY_FLAG[cap];
  if (flag && business[flag] === false) return false;
  return true;
}

/** capability -> the `StorefrontOut` inventory flag that must be true for it to be live. */
const INVENTORY_FLAG = {
  stay_booking: "has_bookable_units",
  venue_booking: "has_bookable_units",
  table_booking: "has_tables",
  appointments: "has_doctors",
};

/** The businesses's ordered, deduped, gated blocks. A `storefront_config.sections` toggle can
 * only HIDE a block that would otherwise show — it can never fabricate a capability section the
 * business lacks, nor a `plans` block when the business has no cards. */
export function sectionsFor(business, config = {}) {
  const order = config.order?.length ? config.order : DEFAULT_ORDER;
  const capabilities = business.capabilities || [];
  const plans = business.plans || [];
  const coverage = config.vertical?.coverage || [];
  const howItWorks = config.profile?.how_it_works || [];
  // Free never accepts online orders/bookings, whatever capabilities the business has toggled on
  // — `business.plan` is a real public field (`BusinessOut.plan`), not owner-only data.
  const seen = new Set();
  return order
    .map((k) => SECTION_REGISTRY[k] || PSEUDO_REGISTRY[k])
    .filter(Boolean)
    // Capability gate BEFORE dedupe: `stay_booking` and `venue_booking` share `key: "booking"`,
    // and `stay_booking` sits first in `DEFAULT_ORDER`. Deduping first (as this used to) kept
    // whichever entry came first in the array regardless of which capability the business
    // actually carries — a `venue_booking`-only business (e.g. a marriage hall) got the
    // `stay_booking`-tagged entry, which then failed ITS OWN gate below and vanished, leaving the
    // business with no booking section at all. Gating first means the surviving entry is always
    // one the business can actually use.
    .filter((s) => {
      if (config.sections?.[s.key] === false) return false;
      if (s.key === "plans") return plans.length > 0;
      if (s.key === "coverage") return coverage.length > 0 && capabilities.includes("enquiry");
      if (s.key === "how_it_works") return howItWorks.length > 0;
      if (s.key === "gallery") return (business.gallery || []).some(Boolean);
      if (s.cap && !isCapabilityLive(business, s.cap)) return false;
      return capabilities.includes(s.cap);
    })
    .filter((s) => {
      if (seen.has(s.key)) return false;
      seen.add(s.key);
      return true;
    });
}

/** Same priority list, but returns up to TWO — the handoff's quick-action
 * strip is multi-pill for a multi-capability business (restaurant: "See menu" + "Reserve a
 * table"; ISP: "Check & see plans" + a coverage strip), where the built shell used to collapse to
 * one generic "Book" CTA (`07_storefront_design_gap.md` §1, Storefront S1 B3; Storefront S5 gap
 * review item 4). Derived from the SAME sections `sectionsFor` actually rendered (so a CTA never
 * scrolls to a section that isn't on the page), primary first, in this fixed priority order:
 * ordering > plans > coverage > appointments > attendance > stay/venue booking > table booking >
 * service visit > enquiry. Each entry's label/icon can be a plain i18n key + lucide icon name, or
 * a function of `business` for the handoff's per-vertical copy (gym "See memberships" vs ISP "See
 * plans", both the same `plans` pseudo-section — `Gym Website.dc.html` / `ISP Website.dc.html`
 * QUICK/COVERAGE strips). */
const QUICK_ACTION_PRIORITY = [
  { key: "menu", labelKey: "storefront.quickAction.seeMenu", icon: "Utensils" },
  {
    key: "plans",
    labelKey: (b) => (b.business_type === "gym" ? "storefront.quickAction.seeMemberships" : "storefront.quickAction.seePlans"),
    // RF-10: the Wifi glyph is the ISP's; a salon/clinic with price cards got a router icon.
    icon: (b) => (b.business_type === "gym" ? "Dumbbell" : b.business_type === "isp" ? "Wifi" : "ListChecks"),
  },
  { key: "coverage", labelKey: "storefront.quickAction.checkCoverage", icon: "MapPin" },
  {
    key: "doctors",
    labelKey: "storefront.quickAction.bookAppointment",
    icon: (b) => (b.business_type === "clinic" ? "Stethoscope" : "CalendarCheck"),
  },
  { key: "attendance", labelKey: "storefront.quickAction.bookTrial", icon: "CalendarCheck" },
  {
    key: "booking",
    labelKey: (b) => (b.capabilities?.includes("stay_booking") ? "storefront.quickAction.checkRooms" : "storefront.quickAction.checkAvailability"),
    icon: "BedDouble",
  },
  { key: "table", labelKey: "storefront.quickAction.reserveTable", icon: "CalendarCheck" },
  { key: "service_visit", labelKey: "storefront.quickAction.bookVisit", icon: "Wrench" },
  { key: "enquiry", labelKey: "storefront.quickAction.enquire", icon: "MessageSquare" },
];

function resolveQuickAction(entry, business, t) {
  const label = typeof entry.labelKey === "function" ? t(entry.labelKey(business)) : t(entry.labelKey);
  const icon = typeof entry.icon === "function" ? entry.icon(business) : entry.icon;
  return { key: entry.key, label, icon };
}

/** Sections whose CTA leads to something the citizen can DO online (order, book, check). A
 * business with none of these and a phone is a phone-first business (RF-10). `plans` counts only
 * where the plans ARE the product (ISP, gym) — on a salon it is a price list, not an action. */
const ACTIONABLE_KEYS = new Set(["menu", "coverage", "doctors", "attendance", "booking", "table", "service_visit"]);

export function quickActionsFor(business, config, t) {
  const rendered = new Set(sectionsFor(business, config).map((s) => s.key));
  const seenKeys = new Set();
  let actions = [];
  for (const entry of QUICK_ACTION_PRIORITY) {
    if (!rendered.has(entry.key) || seenKeys.has(entry.key)) continue;
    seenKeys.add(entry.key);
    actions.push(resolveQuickAction(entry, business, t));
  }
  // RF-10: a business whose only contact path is a phone (nothing actionable online) leads with a
  // `tel:` call CTA instead of a "See plans"/"Enquire" scroll. `href` marks it as a link, not a
  // scroll target; the price-list `plans` pill is dropped in that case since the section is right
  // there on the page.
  const plansAreProduct = ["isp", "gym"].includes(business.business_type);
  const actionable = actions.some((a) => ACTIONABLE_KEYS.has(a.key) || (a.key === "plans" && plansAreProduct));
  if (business.phone && !actionable) {
    actions = [
      { key: "call", label: t("common.call"), icon: "Phone", href: `tel:${business.phone}` },
      ...actions.filter((a) => a.key !== "plans"),
    ];
  }
  return actions.slice(0, 2);
}

/** The one headline action the mobile sticky bar pushes — just the quick-action strip's first
 * entry, so the two never disagree about what a business's "main" CTA is (they used to be two
 * separate priority lists that could pick different sections). `null` when the business has none
 * of the quick-action-eligible capabilities/data. */
export function primaryActionFor(business, config, t) {
  // The mobile bar already carries a round call button, so a `call` CTA is not repeated as its
  // primary pill (it has no section to scroll to).
  const [first] = quickActionsFor(business, config, t).filter((a) => a.key !== "call");
  return first ? { key: first.key, label: first.label } : null;
}

/** R4-10: the `business_type` slug that NAMES this business. A hall that only takes venue bookings
 * (no stays) is stored under the `hotel` preset but is a banquet, not a hotel - label it by what it
 * actually offers. Derived from capabilities already on the payload; nothing new on the wire. */
export function businessKindSlug(business) {
  const caps = business?.capabilities || [];
  if (business?.business_type === "hotel" && caps.includes("venue_booking") && !caps.includes("stay_booking"))
    return "venue";
  return business?.business_type;
}

/** The strip's left-hand descriptor line ("category · locality") — built only from data the
 * business already carries (`business_type`, `address`), never invented copy. */
export function quickActionsDescriptor(business, t, lang) {
  // R3-9: with `t`, the kind is the translated label ("सैलून"); no entry (or no `t`) falls back to
  // the humanised slug.
  const kind = businessKindSlug(business);
  // R7-7: the Hindi label on a Hindi screen, falling back to the English one.
  const custom = String(localized(lang, business, "display_category") || "").trim();
  const humanised = String(kind || "")
    .split(/[_-]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
  const key = `storefront.businessTypes.${kind}`;
  const translated = t && kind ? t(key) : key;
  const category =
    custom || (typeof translated === "string" && translated !== key ? translated : humanised);
  const parts = [category, business.address].filter(Boolean);
  return parts.join(" · ");
}
